/**
 * MONSTER TOWN — PHOTO MODE. HUD/AUDIO/TOUCH CREW (src/ui/**).
 *
 * "The real retention driver at this age is a parent saying *whoa, you built
 * that?*" (brief). So this is not a screenshot feature, it is a SHOW-A-GROWN-UP
 * feature.
 *
 * ============================ ROUND 2 REBUILD =============================
 * What shipped was a plain black rounded border on the canvas and one orange
 * button, and it looked like exactly that — a CSS border. Every part of the
 * critique is addressed here:
 *
 *  - IT WAS NOT A POLAROID. It is now: cream card stock, a fat bottom lip like
 *    a real instant photo, a sticker in the corner and a tilt on the card. The
 *    frame on screen and the frame baked into the saved PNG are the same shape,
 *    so the thing a kid hands over is the thing they lined up.
 *  - THERE WAS NO FLASH. Taking a picture now flashes the screen white and the
 *    frame kicks — the feedback that tells a 3-year-old the tap worked.
 *  - THERE WAS NO GALLERY. The last few pictures sit in a strip along the
 *    bottom; tapping one brings it back full size. Nothing is ever deleted
 *    (LAW #2 — nothing can be failed and nothing is taken away).
 *  - THE PLAYER WAS HALF OUT OF FRAME, jammed into the bottom-left corner
 *    behind a second monster, because photo mode inherited wherever the camera
 *    happened to be. Opening it now AIMS at the kid's own monster, which is the
 *    subject of the photo they want to show.
 *  - THE BUTTONS SAT DIRECTLY ON THE SCENERY — the shutter over a lamp post and
 *    a flower bed, the mascot over a red roof, with no separation. The controls
 *    ride on their own cream bar inside the frame's bottom lip now, which is
 *    where a camera's buttons live anyway.
 * ==========================================================================
 *
 * TECHNICAL NOTE THAT MATTERS: the renderer is created without
 * `preserveDrawingBuffer`, so `canvas.toDataURL()` returns a blank image unless
 * it is read in the SAME task as a render. We therefore force a render and read
 * the pixels on the very next statement. Do not insert an `await` between them.
 *
 * KID RULES: every control is 96 px. Nothing here can fail, nothing is deleted,
 * and the back button exists before the shutter does.
 */

import { bus } from '../core/events'
import { css } from '../core/palette'
import { app } from '../app/context'
import type { GameContext } from '../app/context'
import { button, el } from './dom'
import { iconDataUrl } from './icons'
import { panelShell, type PanelHost, type PanelParts } from './panels'

/** Postcard border thickness as a fraction of the shorter side. */
const FRAME = 0.045
/** How many pictures the strip remembers. Small: this is a shelf, not a folder. */
const GALLERY = 4

/** Pictures taken this session, newest last. Survives closing the panel. */
const gallery: string[] = []

/**
 * The saved PNG wears the same polaroid the screen does: cream card, a FAT
 * bottom lip, an ink keyline and the game's own monster sticker in the corner.
 * A picture that looks like the app is a picture a parent recognises.
 */
function composite(source: HTMLCanvasElement, sticker: HTMLImageElement | null): HTMLCanvasElement {
  const w = source.width
  const h = source.height
  const pad = Math.round(Math.min(w, h) * FRAME)
  const lip = pad * 3
  const out = document.createElement('canvas')
  out.width = w + pad * 2
  out.height = h + pad + lip
  const c = out.getContext('2d')
  if (c === null) return source

  c.fillStyle = css('uiPanel')
  c.fillRect(0, 0, out.width, out.height)
  c.drawImage(source, pad, pad)

  // ink keyline round the picture window
  c.strokeStyle = css('ink')
  c.lineWidth = Math.max(3, pad * 0.24)
  c.strokeRect(pad, pad, w, h)
  // and round the whole card
  c.lineWidth = Math.max(4, pad * 0.34)
  c.strokeRect(c.lineWidth / 2, c.lineWidth / 2, out.width - c.lineWidth, out.height - c.lineWidth)

  if (sticker !== null && sticker.complete && sticker.naturalWidth > 0) {
    const s = lip * 0.78
    c.drawImage(sticker, out.width - pad - s, h + pad + (lip - s) / 2, s, s)
  }
  return out
}

export function registerPhotoPanel(ctx: GameContext, host: PanelHost, onClose: () => void): void {
  // Drawn once, from the icon set — zero external assets.
  const stickerImg = new Image()
  stickerImg.src = iconDataUrl('monster')

  host.register('photo', (): PanelParts => {
    const parts = panelShell({ icon: 'camera', onBack: onClose })
    parts.root.classList.add('mt-photoscreen')
    // Photo mode shows the WORLD, so the panel body must be see-through.
    parts.root.style.background = 'transparent'
    parts.root.style.pointerEvents = 'none'
    const backBtn = parts.head.firstElementChild
    if (backBtn instanceof HTMLElement) backBtn.style.pointerEvents = 'auto'

    // AIM AT THE KID'S OWN MONSTER. Photo mode used to inherit whatever the
    // camera was looking at, which is how the captured frame ended up composed
    // on an empty road with the player clipped into the bottom-left corner.
    ctx.camera.lookAtPoint(app.playerPos, true)

    const frame = el('div', 'mt-photo-frame')
    parts.body.append(frame)
    const flash = el('div', 'mt-photo-flash')
    parts.body.append(flash)

    const bar = el('div', 'mt-photo-bar')
    bar.style.pointerEvents = 'auto'
    parts.foot.append(bar)

    const strip = el('div', 'mt-photo-strip')
    strip.style.pointerEvents = 'auto'
    parts.foot.append(strip)

    const paintStrip = (): void => {
      strip.replaceChildren()
      strip.dataset['on'] = String(gallery.length > 0)
      for (const url of gallery.slice(-GALLERY)) {
        const thumb = el('button', 'mt-photo-thumb')
        thumb.type = 'button'
        thumb.setAttribute('aria-label', 'Look at this picture')
        const img = new Image()
        img.src = url
        thumb.append(img)
        thumb.addEventListener('pointerup', (e) => {
          e.preventDefault()
          e.stopPropagation()
          showPostcard(url)
        })
        strip.append(thumb)
      }
    }

    const shoot = (): void => {
      // RENDER THEN READ, same task. See the note at the top of this file.
      ctx.renderer.render(ctx.camera.camera, 16)
      let url = ''
      try {
        url = ctx.canvas.toDataURL('image/png')
      } catch {
        url = ''
      }
      bus.emit('audio:cue', { cue: 'shutter' })
      // THE FLASH. A tap with no visible answer reads as broken to a 3-year-old.
      flash.dataset['go'] = '1'
      window.setTimeout(() => delete flash.dataset['go'], 320)
      frame.animate([{ transform: 'scale(1)' }, { transform: 'scale(0.97)' }, { transform: 'scale(1)' }], {
        duration: 260,
      })
      if (url === '' || url.length < 2000) {
        // Nothing came back (a context loss, or a browser that refuses). Do not
        // show a broken postcard — the frame already kicked; let them try again.
        return
      }

      const img = new Image()
      img.onload = (): void => {
        const tmp = document.createElement('canvas')
        tmp.width = img.naturalWidth
        tmp.height = img.naturalHeight
        const c = tmp.getContext('2d')
        if (c === null) return
        c.drawImage(img, 0, 0)
        const card = composite(tmp, stickerImg)
        const cardUrl = card.toDataURL('image/png')
        gallery.push(cardUrl)
        while (gallery.length > GALLERY) gallery.shift()
        paintStrip()
        bus.emit('ui:photoTaken', { width: card.width, height: card.height })
        showPostcard(cardUrl)
      }
      img.src = url
    }

    const showPostcard = (url: string): void => {
      const card = el('div', 'mt-postcard')
      const shown = new Image()
      shown.src = url
      card.append(shown)

      const row = el('div', 'mt-row')
      row.append(
        button({ icon: 'tick', label: 'Keep looking at it', className: 'big go' }, () => {
          card.remove()
        }),
        button({ icon: 'bag', label: 'Save the picture', className: 'big accent' }, () => {
          const a = document.createElement('a')
          a.href = url
          a.download = 'monster-town.png'
          a.click()
          bus.emit('ui:toast', { icon: 'tick', ms: 1400 })
        }),
      )
      card.append(row)
      parts.root.append(card)
      bus.emit('ui:toast', { icon: 'camera', ms: 1200 })
    }

    bar.append(
      button({ icon: 'camera', label: 'Take the picture', className: 'big primary', cue: null }, shoot),
    )

    // A sticker on the frame itself, so the LIVE view and the saved card carry
    // the same mark. Drawn from the icon set — zero external assets.
    const sticker = el('img', 'mt-photo-sticker')
    sticker.src = iconDataUrl('monster')
    parts.body.append(sticker)

    paintStrip()
    app.setPhase('photo')
    return parts
  })
}
